/**
 * UploadDemoModal Component
 * ==========================
 * Modal for uploading narrator demo audio with file picker,
 * metadata form, and progress tracking.
 */

import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Upload,
  Music,
  FileAudio,
  X,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Play,
  Pause,
} from "lucide-react";

import narratorService from "@/services/narratorService";

const ALLOWED_EXTENSIONS = [".mp3", ".wav", ".m4a"];
const ALLOWED_MIME_TYPES = ["audio/mpeg", "audio/wav", "audio/mp4", "audio/x-m4a"];
const MAX_SIZE_MB = 50;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

export default function UploadDemoModal({ isOpen, onClose, onUploadComplete }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadState, setUploadState] = useState("idle"); // idle, uploading, done, error
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  // Metadata fields
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("English");
  const [genreTag, setGenreTag] = useState("");
  const [toneTag, setToneTag] = useState("");

  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    if (!file) return "No file selected";

    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(", ")}`;
    }

    if (file.size > MAX_SIZE_BYTES) {
      return `File too large. Maximum: ${MAX_SIZE_MB}MB`;
    }

    return null;
  };

  const handleFileSelect = (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }

    setSelectedFile(file);
    if (audioPreviewUrl) {
      URL.revokeObjectURL(audioPreviewUrl);
    }
    const previewUrl = URL.createObjectURL(file);
    setAudioPreviewUrl(previewUrl);
    setError(null);

    // Auto-fill title from filename if empty
    if (!title) {
      const name = file.name.replace(/\.[^/.]+$/, ""); // Remove extension
      setTitle(name);
    }
  };

  const handleInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    setUploadState("uploading");
    setUploadProgress(0);
    setError(null);

    try {
      const metadata = {
        title: title || selectedFile.name.replace(/\.[^/.]+$/, ""),
        language: language || "English",
        genre_tag: genreTag || null,
        tone_tag: toneTag || null,
      };

      const result = await narratorService.uploadDemo(
        selectedFile,
        metadata,
        (progress) => {
          setUploadProgress(progress);
        }
      );

      setUploadState("done");
      toast.success("Demo uploaded successfully!");

      // Notify parent and close after short delay
      setTimeout(() => {
        if (onUploadComplete) {
          onUploadComplete(result);
        }
        handleClose();
      }, 1500);
    } catch (err) {
      console.error("Upload error:", err);
      const errorMessage =
        err.response?.data?.detail || err.message || "Upload failed";
      setError(errorMessage);
      setUploadState("error");
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    // Reset state
    if (audioPreviewUrl) {
      URL.revokeObjectURL(audioPreviewUrl);
    }
    setAudioPreviewUrl(null);
    setIsPlaying(false);
    setSelectedFile(null);
    setUploadState("idle");
    setUploadProgress(0);
    setError(null);
    setTitle("");
    setLanguage("English");
    setGenreTag("");
    setToneTag("");
    onClose();
  };

  const handleRemoveFile = () => {
    if (audioPreviewUrl) {
      URL.revokeObjectURL(audioPreviewUrl);
    }
    setAudioPreviewUrl(null);
    setIsPlaying(false);
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-white w-[calc(100vw-2rem)] max-w-xl overflow-hidden p-0">
        <div className="max-h-[90vh] overflow-y-auto p-6">
          <DialogHeader className="pr-8">
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-purple-600" />
              Upload Demo
            </DialogTitle>
            <DialogDescription>
              Upload an audio demo clip to showcase your voice
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
          {/* File Drop Zone */}
          {!selectedFile ? (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleBrowseClick}
              className={`
                relative p-8 border-2 border-dashed rounded-xl text-center cursor-pointer
                transition-all duration-200
                ${isDragging
                  ? "border-purple-500 bg-purple-50"
                  : "border-slate-300 bg-slate-50 hover:border-purple-300 hover:bg-purple-50/50"
                }
              `}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/mpeg,audio/wav,audio/mp4,.mp3,.wav,.m4a"
                onChange={handleInputChange}
                className="hidden"
                data-testid="demo-file-input"
              />

              <Music
                className={`w-12 h-12 mx-auto mb-4 ${
                  isDragging ? "text-purple-500" : "text-slate-400"
                }`}
              />
              <p className="text-sm font-medium text-slate-700 mb-1">
                {isDragging ? "Drop audio file here" : "Drag & drop your demo"}
              </p>
              <p className="text-xs text-slate-500 mb-3">or click to browse</p>
              <p className="text-xs text-slate-400">
                MP3, WAV, M4A • Max {MAX_SIZE_MB}MB
              </p>
            </div>
          ) : (
            /* Selected File Preview */
            <div className="w-full overflow-hidden rounded-xl border-2 border-purple-200 bg-purple-50 p-4">
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-pink-500 via-purple-500 to-blue-500">
                  <FileAudio className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">
                    {selectedFile.name}
                  </p>
                  <p className="text-xs text-slate-500">
                    {formatFileSize(selectedFile.size)}
                  </p>
                </div>
                {uploadState === "idle" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleRemoveFile}
                    className="h-9 w-9 flex-shrink-0 p-0 text-slate-500 hover:text-red-500"
                    aria-label="Remove selected audio"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {audioPreviewUrl && uploadState === "idle" && (
                <div className="mt-3 flex min-w-0 items-center gap-3 rounded-lg bg-white/70 px-3 py-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (!audioRef.current) return;

                      if (isPlaying) {
                        audioRef.current.pause();
                      } else {
                        audioRef.current.play();
                      }

                      setIsPlaying(!isPlaying);
                    }}
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 transition hover:bg-purple-200"
                    aria-label={isPlaying ? "Pause audio preview" : "Play audio preview"}
                  >
                    {isPlaying ? (
                      <Pause className="w-4 h-4 text-purple-600" />
                    ) : (
                      <Play className="w-4 h-4 text-purple-600" />
                    )}
                  </button>

                  <span className="min-w-0 truncate text-sm text-slate-600">
                    Preview Audio
                  </span>

                  <audio
                    ref={audioRef}
                    src={audioPreviewUrl}
                    onEnded={() => setIsPlaying(false)}
                  />
                </div>
              )}

              {/* Upload Progress */}
              {uploadState === "uploading" && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-purple-600 font-medium">
                      Uploading...
                    </span>
                    <span className="text-slate-600">{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} className="h-2" />
                </div>
              )}

              {/* Success State */}
              {uploadState === "done" && (
                <div className="mt-3 flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">Upload complete!</span>
                </div>
              )}

              {/* Error State */}
              {uploadState === "error" && error && (
                <div className="mt-3 flex items-center gap-2 text-red-600">
                  <AlertCircle className="w-5 h-5" />
                  <span className="text-sm">{error}</span>
                </div>
              )}
            </div>
          )}

          {/* Metadata Form */}
          {selectedFile && uploadState === "idle" && (
            <div className="space-y-4 pt-2">
              <div>
                <Label htmlFor="demoTitle">Title</Label>
                <Input
                  id="demoTitle"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Demo title"
                  className="w-full"
                  data-testid="demo-title-input"
                />
              </div>

              <div>
                <Label htmlFor="demoLanguage">Language</Label>
                <Input
                  id="demoLanguage"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  placeholder="e.g., English"
                  className="w-full"
                  data-testid="demo-language-input"
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="min-w-0">
                  <Label htmlFor="demoGenre">Genre Tag (optional)</Label>
                  <Input
                    id="demoGenre"
                    value={genreTag}
                    onChange={(e) => setGenreTag(e.target.value)}
                    placeholder="Fiction, Non-fiction..."
                    className="w-full"
                    data-testid="demo-genre-input"
                  />
                </div>
                <div className="min-w-0">
                  <Label htmlFor="demoTone">Tone Tag (optional)</Label>
                  <Input
                    id="demoTone"
                    value={toneTag}
                    onChange={(e) => setToneTag(e.target.value)}
                    placeholder="Warm, Dramatic..."
                    className="w-full"
                    data-testid="demo-tone-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={handleClose}
              className="w-full sm:flex-1"
              disabled={uploadState === "uploading"}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              className="w-full bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 sm:flex-1"
              disabled={!selectedFile || uploadState !== "idle"}
              data-testid="upload-demo-submit-btn"
            >
              {uploadState === "uploading" ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Demo
                </>
              )}
            </Button>
          </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
